import { useGeneral } from '@utils/general';

const { normalizeString } = useGeneral();

type HighlightOptions = {
  clearExistingHighlights?: boolean;
  ignoreSelectors?: string[];
  wholeWordsOnly?: boolean;
};

/**
 * Hook/factory that returns helpers for highlighting text within a DOM subtree.
 *
 * How it works (high level):
 * - Normalize the search query (via `normalize()` from @utils/general).
 * - Walk all text nodes under a root element.
 * - For each text node:
 *   - Build a "normalized" version of the text (lowercase + strip diacritics).
 *   - Keep a mapping from each normalized character back to the original string index.
 *   - Find all matches in the normalized text.
 *   - Convert normalized match indices back to original string ranges.
 *   - Replace the text node with a fragment containing text + <mark> wrappers.
 *
 * Notes:
 * - Matches are only found within a single text node (it does not match across node boundaries).
 * - Existing highlights are cleared before applying new ones.
 */
export const useHighlight = (highlightClass: string) => {
  /**
   * Returns how many UTF-16 code units the character at `index` uses.
   * - Most characters are 1 code unit.
   * - Characters outside the BMP (e.g. many emoji) are surrogate pairs => 2 code units.
   *
   * This is important because JS string indices are based on UTF-16 code units, not Unicode code points.
   */
  const getCodeUnitCharLen = (text: string, index: number): number => {
    const cp = text.codePointAt(index);
    return cp !== undefined && cp > 0xffff ? 2 : 1;
  };

  /**
   * Builds a normalized string and a reverse mapping to the original string indices.
   *
   * Example:
   * - Original: "Café"
   * - Normalized (lowercase + NFD + remove diacritics): "cafe"
   * - Map: each char in "cafe" maps to the original index where it came from
   *
   * Why the map is needed:
   * - Normalization can change length (e.g., diacritics removed, decomposed characters, etc.)
   * - We still need correct start/end indices in the original text for slicing and wrapping.
   */
  const buildNormalizedWithMap = (text: string): { norm: string; map: number[] } => {
    let norm = '';
    let i = 0; // code unit index
    const map: number[] = [];

    for (const ch of text) {
      const originalIndex = i;

      // Normalize per character:
      // - lowercase for case-insensitive matches
      // - NFD splits base letters and diacritics (e.g., "é" => "e" + "́")
      //     What that means:
      //     Many “single” characters can also be represented as base character + combining mark.
      //     Example: é can be:
      //     one code point: U+00E9 (é)
      //     or two code points: U+0065 (e) + U+0301 (combining acute accent)
      //     With NFD, Unicode chooses the decomposed form when possible.
      // - remove diacritic marks, making matching accent-insensitive
      const normPiece = ch
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');

      for (const np of normPiece) {
        norm += np;
        map.push(originalIndex);
      }

      // `ch.length` is 1 for BMP chars, 2 for surrogate pairs.
      i += ch.length;
    }

    return { norm, map };
  };

  /**
   * Finds all (non-overlapping) occurrences of `needle` within `haystack`.
   * Returns the start indices of each match.
   */
  const isWordCharacter = (char: string | undefined): boolean => {
    return char !== undefined && /[\p{L}\p{N}_-]/u.test(char);
  };

  const isWholeWordMatch = (haystack: string, startIndex: number, needleLength: number): boolean => {
    const previousChar = startIndex > 0 ? haystack[startIndex - 1] : undefined;
    const nextChar = haystack[startIndex + needleLength];

    return !isWordCharacter(previousChar) && !isWordCharacter(nextChar);
  };

  const findAllOccurrences = (haystack: string, needle: string, wholeWordsOnly = false): number[] => {
    if (!needle) {
      return [];
    }
    
    const hits: number[] = [];
    let start = 0;

    while (true) {
      const idx = haystack.indexOf(needle, start);
      if (idx === -1) break;

      if (!wholeWordsOnly || isWholeWordMatch(haystack, idx, needle.length)) {
        hits.push(idx);
      }

      // Advance at least 1 char to prevent infinite loops if needle length is 0.
      // Using needle.length makes matches non-overlapping.
      start = idx + Math.max(1, needle.length);
    }

    return hits;
  };

  /**
   * Merges overlapping (and touching) ranges.
   * Input/output ranges are [start, end) (end is exclusive).
   *
   * This prevents nested/adjacent <mark> elements when matches overlap after mapping.
   */
  const mergeRanges = (ranges: Array<[number, number]>): Array<[number, number]> => {
    if (ranges.length === 0) {
      return [];
    }

    ranges.sort((a, b) => a[0] - b[0]);

    const merged: Array<[number, number]> = [ranges[0]];

    for (let i = 1; i < ranges.length; i++) {
      const [s, e] = ranges[i];
      const last = merged[merged.length - 1];

      // If the new range starts before (or exactly at) the end of the last range, extend the last range to cover it.
      if (s <= last[1]) {
        last[1] = Math.max(last[1], e);
      } else {
        merged.push([s, e]);
      }
    }

    return merged;
  };

  /**
   * Removes all existing highlights (<mark class="{highlightClass}">) under `root`.
   * Each mark is replaced with a plain text node containing its text content.
   *
   * After replacement, parent.normalize() merges adjacent text nodes so future runs
   * don't operate on fragmented text nodes created by prior highlighting.
   */
  const clearHighlights = (root: Element): void => {
    const marks = root.querySelectorAll(`mark.${highlightClass}`);

    marks.forEach((m) => {
      const parent = m.parentNode;

      if (!parent) {
        return;
      }

      parent.replaceChild(document.createTextNode(m.textContent ?? ''), m);

      // Merge adjacent text nodes after replacements (DOM normalize, not string normalize).
      (parent as HTMLElement).normalize();      
    });
  };

  /**
   * Collects all text nodes under `root` that are not:
   * - Whitespace-only
   * - Descendants of existing highlights (e.g., inside <mark class="{highlightClass}">)
   * - Descendants of elements matching any of the `ignoreSelectors`
   *
   * This ensures we only attempt to highlight visible text that isn't already highlighted or meant to be ignored.
   */
  const collectTextNodes = (root: HTMLElement, ignoreSelectors?: string[]): Text[] => {
    const ignoredSelector = ignoreSelectors?.join(', ');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        const parentEl = (node as Text).parentElement;

        if (parentEl?.closest(`mark.${highlightClass}`)) {
          return NodeFilter.FILTER_REJECT;
        }

        if (ignoredSelector && parentEl?.closest(ignoredSelector)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      }
    });

    const textNodes: Text[] = [];
    let node: Node | null;

    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    return textNodes;
  };

  const rangesOverlap = (rangeA: [number, number], rangeB: [number, number]): boolean => {
    return rangeA[0] < rangeB[1] && rangeB[0] < rangeA[1];
  };

  /**
   * For a given text node, builds the original [start, end) ranges for all matches of any of the `needles`.
   *
   * Steps:
   * - For each needle, find all occurrences in the normalized text.
   * - For each occurrence, map the normalized indices back to original string indices using `map`.
   * - Skip any ranges that overlap with previously found ranges (to prevent nested highlights).
   * - Merge overlapping/touching ranges at the end to ensure clean highlighting.
   */
  const buildRangesForNeedles = (
    text: string,
    norm: string,
    map: number[],
    needles: string[],
    wholeWordsOnly: boolean
  ): Array<[number, number]> => {
    const ranges: Array<[number, number]> = [];

    for (const needle of needles) {
      const hitStarts = findAllOccurrences(norm, needle, wholeWordsOnly);

      if (hitStarts.length === 0) {
        continue;
      }

      for (const startNormIdx of hitStarts) {
        const endNormIdx = startNormIdx + needle.length - 1;
        const startOrig = map[startNormIdx];
        const lastOrigStart = map[Math.min(endNormIdx, map.length - 1)];
        const endOrig = lastOrigStart + getCodeUnitCharLen(text, lastOrigStart);
        const nextRange: [number, number] = [startOrig, endOrig];

        if (ranges.some((existingRange) => rangesOverlap(existingRange, nextRange))) {
          continue;
        }

        ranges.push(nextRange);
      }
    }

    return mergeRanges(ranges);
  };

  /**
   * Convenience for single query; just calls the multi-query version with an array of one item.
   */
  const highlightInElement = (root: HTMLElement, query: string, options: HighlightOptions = {}): void => {
    highlightQueriesInElement(root, [query], options);
  };

  /**
   * Highlights all occurrences of `queries` inside `root` by wrapping matches with `<mark>`.
   *
   * This is more efficient than calling `highlightInElement` multiple times because it walks the DOM once
   * and merges overlapping matches from different queries.
   *
   * Steps:
   * 1) Optionally clear previous highlights.
   * 2) Normalize all queries and skip empty values; if none remain, stop.
   * 3) Collect text nodes under `root` \(excluding whitespace-only nodes, ignored selectors, and existing highlights\).
   * 4) For each text node:
   *    - Build normalized text and index mapping.
   *    - Find all matches for all normalized queries \(optionally whole-word only\).
   *    - Map matches back to original `[start, end)` ranges.
   *    - Merge ranges and rebuild the node as text/`<mark>` segments.
   */
  const highlightQueriesInElement = (root: HTMLElement, queries: string[], options: HighlightOptions = {}): void => {
    const clearExistingHighlights = options.clearExistingHighlights ?? true;

    // Clear old highlights unless the caller wants to accumulate matches across multiple calls.
    if (clearExistingHighlights) {
      clearHighlights(root);
    }

    const needles = queries
      .map((query) => normalizeString(query))
      .filter((query): query is string => Boolean(query));
    const wholeWordsOnly = options.wholeWordsOnly ?? false;

    if (needles.length === 0) {
      return;
    }

    const textNodes = collectTextNodes(root, options.ignoreSelectors);

    for (const textNode of textNodes) {
      const text = textNode.nodeValue ?? '';

      // Normalize the text node and keep a mapping back to original indices.
      const { norm, map } = buildNormalizedWithMap(text);

      const merged = buildRangesForNeedles(text, norm, map, needles, wholeWordsOnly);

      if (merged.length === 0) {
        continue;
      }

      // Rebuild this text node as a fragment: [text][mark][text]...
      const fragment = document.createDocumentFragment();
      let cursor = 0;

      for (const [s, e] of merged) {
        // Add any plain text before the match.
        if (s > cursor) {
          fragment.appendChild(document.createTextNode(text.slice(cursor, s)));
        }

        // Add the highlighted match.
        const mark = document.createElement('mark');
        mark.className = highlightClass;
        mark.textContent = text.slice(s, e);
        fragment.appendChild(mark);

        // Continue after the match.
        cursor = e;
      }

      // Add any trailing text after the last match.
      if (cursor < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(cursor)));
      }

      // Replace the original text node with the new fragment.
      textNode.parentNode?.replaceChild(fragment, textNode);
    }
  };

  /**
   * Public API returned by the hook/factory.
   * - highlightClass: class applied to created <mark> elements
   * - clearHighlights: remove existing highlights under a root element
   * - highlightInElement: apply highlights for a query under a root element
   */
  return {
    highlightClass,
    clearHighlights,
    highlightInElement,
    highlightQueriesInElement
  };
};
